/**
 * Cookie helpers for the access_token cookie.
 *
 * Why these exist:
 * - Each main LIS platform is served at its own base href (`/lis/admin`,
 *   `/lis/receptionist`, ...). The access token belongs to a single
 *   platform, so its cookie path must match that platform's base href so
 *   it is only sent to that platform.
 * - On the source side (login redirecting to a destination platform) the
 *   path is derived from the destination platform's URL.
 * - On the destination side (the platform that owns the token) the path
 *   is read from the `<base href>` element of the current document.
 *
 * In local development each platform runs on its own port, so the cookie
 * cannot follow a redirect across origins — callers in that environment
 * still pass tokens via URL query params and the receiving guard sets
 * the cookie on the destination origin.
 */
const ACCESS_TOKEN_COOKIE = 'access_token';

/**
 * Returns the cookie path for the currently running platform, derived
 * from the `<base href>` set in `index.html`. Falls back to `/` if no
 * base element is present.
 */
export function getCurrentPlatformCookiePath(): string {
    const base = document.querySelector('base');
    const href = base?.getAttribute('href');

    if (href) {
        return normalizeCookiePath(href);
    }

    return '/';
}

/**
 * Returns the cookie path for a destination platform, derived from its
 * full URL (e.g. `https://revolab.global/lis/receptionist` →
 * `/lis/receptionist`). Used by the source platform when setting the
 * cookie before a redirect.
 */
export function getCookiePathFromUrl(platformUrl: string): string {
    try {
        const url = new URL(platformUrl);
        return normalizeCookiePath(url.pathname);
    } catch {
        return '/';
    }
}

export function getAccessTokenCookie(): string | null {
    const cookies = document.cookie ? document.cookie.split('; ') : [];

    for (const cookie of cookies) {
        const eqIdx = cookie.indexOf('=');

        if (eqIdx < 0) {
            continue;
        }

        const name = cookie.slice(0, eqIdx);

        if (name === ACCESS_TOKEN_COOKIE) {
            const rawValue = cookie.slice(eqIdx + 1);
            return rawValue ? decodeURIComponent(rawValue) : null;
        }
    }

    return null;
}

export function setAccessTokenCookie(token: string, path: string): void {
    document.cookie = `${ACCESS_TOKEN_COOKIE}=${token}; path=${path}; secure; sameSite=strict`;
}

export function clearAccessTokenCookie(path: string): void {
    document.cookie = `${ACCESS_TOKEN_COOKIE}=; path=${path}; secure; sameSite=strict; max-age=0`;
}

function normalizeCookiePath(rawPath: string): string {
    if (!rawPath) {
        return '/';
    }

    // Cookie paths should not have a trailing slash unless it is the root.
    const trimmed = rawPath.replace(/\/+$/, '');

    return trimmed.length === 0 ? '/' : trimmed;
}
